import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { auditApi, type PaginatedAuditResponse } from '@/api/admin/audit';

interface AuditModalUser {
  id: string;
  name: string;
}

interface UseAuditModalReturn {
  isOpen: boolean;
  user: AuditModalUser | null;
  currentPage: number;
  auditData: PaginatedAuditResponse | undefined;
  isLoading: boolean;
  openModal: (user: AuditModalUser) => void;
  closeModal: () => void;
  handlePageChange: (page: number) => void;
}

export function useAuditModal(): UseAuditModalReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState<AuditModalUser | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const { data: auditData, isLoading } = useQuery({
    queryKey: ['audit', user?.id, currentPage],
    queryFn: () => auditApi.findByEntity(user?.id!, { page: currentPage, limit: 10 }),
    enabled: !!user?.id && isOpen,
  });

  const openModal = useCallback((selectedUser: AuditModalUser) => {
    setUser(selectedUser);
    setCurrentPage(1);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setUser(null);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  return {
    isOpen,
    user,
    currentPage,
    auditData,
    isLoading,
    openModal,
    closeModal,
    handlePageChange,
  };
}
